/* eslint-disable prefer-destructuring */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/naming-convention */
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import LocationsSearch from '@components/Hotels/LocationsSearch';
import { SingleValue } from 'react-select';
import Layout from '@components/Layout/Layout';
import { Location } from '@components/Hotels/types';
import RoutingPath from '@constants/routingPath';
import { useRouter } from 'next/router';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { srpSearch } from '@events/SRP';
import SRPFilters from '@components/SRPFilters/SRPFilters';
import BottomDrawer from '@components/common/BottomDrawer/BottomDrawer';
import useIsMobile from '@hooks/useIsMobile';
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import {
  SrpFilters as SrpFiltersType,
  SrpFilterCount,
  SrpFilterOptions,
} from '@customTypes/srp-filters';
import getDefaultLocation from '@helpers/srpHelpers';
import LOCATION_TYPE from '@constants/locationTypes';
import { getFilterOptions } from '@utils/services';
import {
  FILTER_CATEGORY_GUEST_RATING,
  FILTER_CATEGORY_STAR_CLASSIFICATION,
  FILTER_CATEGORY_VALUES_SEARCH_V5,
  FILTER_CATEGORY_HOTEL_VIBES,
} from '@constants/filterValues';
import useNextQueryParam from '@hooks/useNextQueryParam';
import {
  convertFilterUrlParams,
  convertShowOnlyFilterUrlParams,
} from '@helpers/convertFilterUrlParams';
import { useEvents } from '@events/EventsProvider';
import { VIEW_SEARCH_RESULTS_PAGE } from '@constants/amplitudeEvents';
import HotelApi from '@customTypes/hotel-api';
import { isArray } from 'lodash';

interface SearchOption {
  readonly value: string;
  readonly label: string;
}

type Props = {
  name: string | string[] | undefined;
  searchWithoutQuery: boolean;
  setTitleHotelName?: Dispatch<SetStateAction<string>>;
};

const defaultProps = {
  setTitleHotelName: () => null,
};

export default function Search({ name, searchWithoutQuery, setTitleHotelName }: Props) {
  const [input, setInput] = useState<string>('');
  const [value, setValue] = useState<SingleValue<SearchOption>>();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [locations, setLocations] = useState<Location[]>([]);
  const [srpFilters, setSrpFilters] = useState<SrpFiltersType | undefined>();
  const [filterOptions, setFilterOptions] = useState<SrpFilterOptions | undefined>();
  const [localClearAll, setLocalClearAll] = useState<boolean>(false);
  const [isRefreshData, setIsRefreshData] = useState<boolean>(false);
  const [data, setData] = useState<any>({
    hotels: [],
    stageData: { currentStage: 1, page: 0 },
    previousInfo: null,
    offset: 0,
    page: 0,
    forceReload: true,
  });
  const [amplitudePageViewEventSent, setAmplitudePageViewEventSent] = useState<boolean>(false);

  const router = useRouter();
  const isMobile = useIsMobile();
  const { setItem, getItem, removeItem } = useSessionStorage();
  const globalState = useContext(store);
  const { state: stateObj, dispatch } = globalState;
  const { isFiltersExpanded, onlyShowAvailableHotels, searchResults } = stateObj;
  const [localShowOnlyAvailableHotels, setLocalShowOnlyAvailableHotels] =
    useState<boolean>(onlyShowAvailableHotels);
  const { state, country_name, city_id, hotel_name } = router.query;
  const filters = useNextQueryParam('filters');
  const { amplitude, track } = useEvents();
  const [isLoading, setIsLoading] = useState<boolean>(!!data);

  useEffect(() => {
    if (filters && filters !== '') {
      removeItem(sessionStorageKeys.SRP_FILTERS);
      removeItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
    }
  }, [filters, removeItem]);

  const closeFilters = () => {
    dispatch({
      type: actionTypes.OPEN_FILTERS,
      payload: false,
    });
  };

  const resetData = () => {
    setData({
      hotels: [],
      stageData: { currentStage: 1, page: 0 },
      previousInfo: null,
      offset: 0,
      page: 0,
      forceReload: true,
    });
  };

  const formFilterObject = (allFilterOptions: SrpFilterOptions) => {
    const filterObject: any = { url: window.location.href };
    const labels: any = {};
    for (const [key, optionValue] of Object.entries(allFilterOptions)) {
      (filterObject as any)[key] = {};
      (optionValue as any).forEach((nestedValue: any) => {
        const filterName = nestedValue.name.toString().toLowerCase();
        labels[nestedValue.name] = nestedValue.description;
        (filterObject as any)[key][filterName] = false;
        if (key === FILTER_CATEGORY_STAR_CLASSIFICATION && filterName === 'any') {
          (filterObject as any)[key][filterName] = true;
        }
      });
    }
    return { filterObject, labels };
  };

  const toggleAvailableHotelsCheckbox = useCallback(
    (isToggled: boolean) => {
      dispatch({
        type: actionTypes.TOGGLE_ONLY_SHOW_AVAILABLE_HOTELS,
        payload: isToggled,
      });
    },
    [dispatch],
  );

  const handleChangeHotelsAvailable = useCallback(
    (isAvailable: boolean | null = null) => {
      if (!isMobile) {
        setItem(
          sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER,
          (isAvailable ?? !onlyShowAvailableHotels).toString(),
        );
        toggleAvailableHotelsCheckbox(isAvailable ?? !onlyShowAvailableHotels);
      }
      setLocalClearAll(false);
      setLocalShowOnlyAvailableHotels(isAvailable ?? !onlyShowAvailableHotels);
    },
    [isMobile, onlyShowAvailableHotels, setItem, toggleAvailableHotelsCheckbox],
  );

  const filterCount = useMemo(() => {
    // default counts object
    const counts = {
      star_classification: 0,
      amenities: 0,
      totalCount: 0,
      hotel_vibes: 0,
    };

    if (srpFilters) {
      for (const [key, val] of Object.entries(srpFilters)) {
        let count = 0;
        if (key && val && key !== 'url') {
          for (const [nestedKey, nestedValue] of Object.entries(val as Object)) {
            if (nestedKey && nestedValue && nestedKey !== '0') {
              count += 1;
              counts[key as keyof SrpFilterCount] = count;
            }
          }
        }
      }
    }

    return counts;
  }, [srpFilters]);

  const updateTotalFilterCount = useCallback(() => {
    let count = 0;
    if (filterCount) {
      count = Object.values(filterCount).reduce((a, b) => a + b, 0);
    }
    if (localShowOnlyAvailableHotels) {
      count += 1;
    }
    dispatch({
      type: actionTypes.UPDATE_FILTER_COUNT,
      payload: count,
    });
  }, [dispatch, filterCount, localShowOnlyAvailableHotels]);

  const loadFilterData = async () => {
    const allFilterOptions: SrpFilterOptions = await getFilterOptions();
    const { filterObject } = formFilterObject(allFilterOptions);
    setFilterOptions(allFilterOptions);
    dispatch({
      type: actionTypes.AVAILABLE_SEARCH_FILTER_OPTIONS,
      payload: allFilterOptions,
    });

    setSrpFilters(filterObject);

    const storedFilters =
      getItem(sessionStorageKeys.SRP_FILTERS) || convertFilterUrlParams(filters, filterObject);
    const showOnlyAvailableFilter =
      getItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER) ||
      convertShowOnlyFilterUrlParams(filters, filterObject);
    if (storedFilters || showOnlyAvailableFilter) {
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        if (parsedFilters.url === window.location.href) {
          setSrpFilters(parsedFilters);

          dispatch({
            type: actionTypes.APPLY_SEARCH_FILTER,
            payload: parsedFilters,
          });

          handleChangeHotelsAvailable(showOnlyAvailableFilter === 'true');
          if (isMobile) {
            setIsRefreshData(true);
          }
          return;
        }
      } else if (showOnlyAvailableFilter) {
        handleChangeHotelsAvailable(showOnlyAvailableFilter === 'true');
        if (isMobile) {
          setIsRefreshData(true);
        }
        return;
      }

      removeItem(sessionStorageKeys.SRP_FILTERS);
      removeItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
      updateTotalFilterCount();
    }
  };

  const setupFilters = async () => {
    const allFilterOptions: SrpFilterOptions = await getFilterOptions();
    const { filterObject } = formFilterObject(allFilterOptions);
    setFilterOptions(allFilterOptions);

    dispatch({
      type: actionTypes.AVAILABLE_SEARCH_FILTER_OPTIONS,
      payload: allFilterOptions,
    });

    dispatch({
      type: actionTypes.APPLY_SEARCH_FILTER,
      payload: filterObject,
    });

    setSrpFilters(filterObject);
    if (!isMobile || !isFiltersExpanded) {
      dispatch({
        type: actionTypes.UPDATE_FILTER_COUNT,
        payload: 0,
      });
      dispatch({
        type: actionTypes.TOGGLE_ONLY_SHOW_AVAILABLE_HOTELS,
        payload: false,
      });
      setLocalShowOnlyAvailableHotels(false);
      removeItem(sessionStorageKeys.SRP_FILTERS);
      removeItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
    }
  };

  const clearFilters = () => {
    setupFilters();
    const url = new URL(window.location.href);
    url.searchParams.delete('filters');
    url.searchParams.delete('nodate');
    url.searchParams.delete('pid');
    url.searchParams.delete('shownew');
    window.location.href = url.toString();
  };

  const setTypeAheadValue = (
    locationTypes: string[],
    defaultLocation: Location,
    url: string,
    id: number = -3,
    hotelName: string = '',
  ) => {
    const storedLocation = getItem(sessionStorageKeys.SEARCHED_LOCATION);
    const storedLocationObj = storedLocation ? JSON.parse(storedLocation) : null;
    if (
      storedLocationObj &&
      locationTypes.indexOf(storedLocationObj.type) > -1 &&
      (storedLocationObj.url === url || storedLocationObj.id === id)
    ) {
      setInput(storedLocationObj.name);
      setValue({
        label: storedLocationObj.name,
        value: storedLocationObj.name,
      });
      return storedLocationObj;
    }
    if (hotelName) {
      setInput(hotelName);
      setValue({
        label: hotelName,
        value: hotelName,
      });
      return defaultLocation;
    }
    removeItem(sessionStorageKeys.SEARCHED_LOCATION);
    return defaultLocation;
  };

  useEffect(() => {
    (async () => {
      if (!searchWithoutQuery) {
        // State search
        if (state) {
          const url = `/hotel-day-passes?state=${state}`;
          let defaultLocation = getDefaultLocation(LOCATION_TYPE.STATE, url, '', 1, null);
          defaultLocation = setTypeAheadValue([LOCATION_TYPE.STATE], defaultLocation, url);
          setSelectedLocation(defaultLocation);
          return;
        }

        // Country search
        if (country_name) {
          const countryName = country_name.toString();
          const countryCodeJoined = countryName.split(' ').join('+');
          const url = `/hotel-day-passes?country_name=${countryCodeJoined}`;
          let defaultLocation = getDefaultLocation(
            LOCATION_TYPE.COUNTRY,
            url,
            countryName,
            1,
            null,
          );
          defaultLocation = setTypeAheadValue([LOCATION_TYPE.COUNTRY], defaultLocation, url);
          setSelectedLocation(defaultLocation);
          return;
        }

        // Single hotel search
        if (city_id && hotel_name) {
          const hotelNameEncoded = encodeURIComponent(hotel_name as string);
          const url = `/hotel-day-passes?hotel_name=${hotelNameEncoded}&city_id=${city_id}${
            filters ? `&filters=${filters}` : ''
          }`;
          const defaultLocation = getDefaultLocation(
            LOCATION_TYPE.CITY_HIGHLIGHTED,
            url,
            hotel_name ? hotel_name.toString() : '',
            -1,
            city_id ? +city_id : -1,
          );
          setTypeAheadValue(
            [LOCATION_TYPE.HOTEL],
            defaultLocation,
            '',
            -1,
            hotel_name ? hotel_name.toString() : '',
          );
          setSelectedLocation(defaultLocation);
          return;
        }

        // City search with id
        if (city_id) {
          const cityId = city_id.toString();
          const url = `/hotel-day-passes?city_id=${cityId}${filters ? `&filters=${filters}` : ''}`;
          const defaultLocation = getDefaultLocation(LOCATION_TYPE.CITY, url, '', +cityId, null);
          setSelectedLocation(defaultLocation);
          return;
        }

        // Default city search
        if (name) {
          const splitSearch = name.toString().split('-');
          const cityId = splitSearch[splitSearch.length - 1];
          const url = `/hotel-day-passes/${name}${filters ? `?filters=${filters}` : ''}`;
          let defaultLocation = getDefaultLocation(LOCATION_TYPE.CITY, url, '', +cityId, null);
          defaultLocation = setTypeAheadValue(
            [LOCATION_TYPE.CITY, LOCATION_TYPE.ALIAS],
            defaultLocation,
            url,
            +cityId,
          );
          setSelectedLocation(defaultLocation);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, country_name, city_id, hotel_name, name, searchWithoutQuery]);

  useEffect(() => {
    if (locations && locations.length > 0 && value && value.value) {
      const selectedLocationValue = locations.filter((l) => l.name === value.value)[0];
      if (selectedLocationValue) {
        setItem(sessionStorageKeys.SEARCHED_LOCATION, JSON.stringify(selectedLocationValue));

        if (selectedLocationValue.type === 'hotel') {
          const locationEncoded = encodeURIComponent(selectedLocationValue.name);
          router.push(
            `${RoutingPath.SRP}?hotel_name=${locationEncoded}&city_id=${
              selectedLocationValue.parent_id
            }&highlighted=true${filters ? `&filters=${filters}` : ''}`,
          );
          return;
        }
        const qs =
          selectedLocationValue.url.indexOf('?') === -1
            ? `${filters ? `?filters=${filters}` : ''}`
            : `${filters ? `&filters=${filters}` : ''}`;
        if (selectedLocation && selectedLocation.name !== selectedLocationValue.name) {
          router.push(`${selectedLocationValue.url}${qs}`);
        } else if (
          selectedLocationValue.type !== LOCATION_TYPE.STATE ||
          selectedLocationValue.type !== LOCATION_TYPE.COUNTRY
        ) {
          router.push(`${selectedLocationValue.url}${qs}`);
        } else {
          router.push(`${selectedLocationValue.url}${qs}`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, value]);

  useEffect(() => {
    if ((selectedLocation || searchWithoutQuery) && router.isReady) {
      // Event
      if (selectedLocation) srpSearch(selectedLocation.name);
      const storedFilters = getItem(sessionStorageKeys.SRP_FILTERS) || filters;

      const showOnlyAvailableFilter = getItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
      if (storedFilters || showOnlyAvailableFilter) {
        loadFilterData();
        return;
      }
      setupFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, router.isReady, searchWithoutQuery]);

  // -------------------------------------------------------------------
  // Query Parameters used for Algolia Search --------------------------
  // -------------------------------------------------------------------
  // This is dynamically generated based on the search filters (checkboxes)
  // selected.
  // -------------------------------------------------------------------
  const algoliaSearchFilters = useMemo(() => {
    const algoliaFilters = [];

    if (srpFilters) {
      for (const [key, val] of Object.entries(srpFilters)) {
        const categoryFilterParam: string[] = [];
        if (key && val && key !== 'url') {
          for (const [nestedKey, nestedValue] of Object.entries(val as Object)) {
            if (
              nestedKey &&
              nestedValue &&
              nestedKey !== '0' &&
              nestedKey.toLowerCase() !== 'any'
            ) {
              if (key === FILTER_CATEGORY_GUEST_RATING) {
                categoryFilterParam.push(`${FILTER_CATEGORY_VALUES_SEARCH_V5[key]}${nestedKey}`);
              } else if (key === FILTER_CATEGORY_STAR_CLASSIFICATION) {
                categoryFilterParam.push(`${FILTER_CATEGORY_VALUES_SEARCH_V5[key]}${nestedKey}`);
              } else if (key === FILTER_CATEGORY_HOTEL_VIBES) {
                // we need to search for both primary and secondary vibes
                categoryFilterParam.push(
                  ...[`vibes.primary:${nestedKey}`, `vibes.secondary:${nestedKey}`],
                );
              } else {
                categoryFilterParam.push(`${FILTER_CATEGORY_VALUES_SEARCH_V5[key]}:${nestedKey}`);
              }
            }
          }
          if (categoryFilterParam.length > 0) algoliaFilters.push(categoryFilterParam);
        }
      }
    }

    return algoliaFilters;
  }, [srpFilters]);

  useEffect(() => {
    if (!amplitudePageViewEventSent && amplitude && searchResults) {
      const search = isArray(searchResults) ? searchResults[0] : searchResults;
      const hotels = search.hotels;
      const srpType = (() => {
        if (state) return 'state';
        if (country_name) return 'country';
        if (hotel_name && city_id) return 'hotel';
        return 'city';
      })();

      track(
        VIEW_SEARCH_RESULTS_PAGE,
        {
          srp_type: srpType,
          hotel_sort_order: hotels.map((hotel) => hotel.name),
          hot_spot_hotels:
            search.hot_spot_hotels?.map(
              (hotSpotHotel) =>
                hotels.find((hotel: HotelApi) => hotel.id === hotSpotHotel.hotel_id)?.name,
            ) || [],
        },
        { withURLandTitle: true },
      );

      setAmplitudePageViewEventSent(true);
    }
  }, [
    amplitude,
    amplitudePageViewEventSent,
    searchResults,
    track,
    state,
    country_name,
    hotel_name,
    city_id,
  ]);

  return (
    <Layout
      userLocation={null} // set as null for the moment (search-experiment)
      searchBarEnabled
      searchProps={{
        value,
        setValue,
        startDate,
        setStartDate,
        locations,
        setLocations,
        input,
        searchWithoutQuery,
      }}
      noFooter={isLoading}
    >
      <main className="mx-auto flex justify-center">
        {!isMobile ? (
          <SRPFilters
            srpFilters={srpFilters}
            filterOptions={filterOptions}
            setSrpFilters={setSrpFilters}
            closeFilters={closeFilters}
            isFiltersExpanded={isFiltersExpanded}
            clearFilters={clearFilters}
            filterCount={filterCount}
            localShowOnlyAvailableHotels={localShowOnlyAvailableHotels}
            setLocalShowOnlyAvailableHotels={setLocalShowOnlyAvailableHotels}
            selectedLocation={selectedLocation}
            localClearAll={localClearAll}
            setLocalClearAll={setLocalClearAll}
            isRefreshData={isRefreshData}
            setIsRefreshData={setIsRefreshData}
            handleChangeHotelsAvailable={handleChangeHotelsAvailable}
            toggleAvailableHotelsCheckbox={toggleAvailableHotelsCheckbox}
          />
        ) : (
          <BottomDrawer isOpen={isFiltersExpanded} setIsOpen={closeFilters} isTopOffset>
            <SRPFilters
              srpFilters={srpFilters}
              filterOptions={filterOptions}
              setSrpFilters={setSrpFilters}
              closeFilters={closeFilters}
              isFiltersExpanded={isFiltersExpanded}
              clearFilters={clearFilters}
              filterCount={filterCount}
              localShowOnlyAvailableHotels={localShowOnlyAvailableHotels}
              setLocalShowOnlyAvailableHotels={setLocalShowOnlyAvailableHotels}
              selectedLocation={selectedLocation}
              localClearAll={localClearAll}
              setLocalClearAll={setLocalClearAll}
              isRefreshData={isRefreshData}
              setIsRefreshData={setIsRefreshData}
              handleChangeHotelsAvailable={handleChangeHotelsAvailable}
              toggleAvailableHotelsCheckbox={toggleAvailableHotelsCheckbox}
            />
          </BottomDrawer>
        )}

        <div className="flex flex-col mb-10 max-w-1100 w-full relative d:mt-2 d:ml-6">
          {srpFilters && (
            <LocationsSearch
              data={data}
              setData={setData}
              resetData={resetData}
              selectedLocation={selectedLocation}
              searchWithoutQuery={searchWithoutQuery}
              setTitleHotelName={setTitleHotelName}
              filterParams={algoliaSearchFilters}
              clearFilters={clearFilters}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          )}
        </div>
      </main>
    </Layout>
  );
}

Search.defaultProps = defaultProps;
